import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { getQueryPayloadAndKey } from '@/utils/queryKey';
import { TIME_INTERVALS } from '../../constants/queries';
import { ItemType } from '../../models/ItemType';
import { Message } from '../../models/Message';
import { GetItemsAsyncArgs, getItemsAsync } from '../../network/useNetwork';
import { QueryPrefix, queryClient, setQueryDataIfOnline } from '../../queryClient';
import { Direction } from '../../views/Layout/Direction';

export const MESSAGES_LIMIT = 10;

type PayloadOptions = Pick<GetItemsAsyncArgs, 'limit' | 'offset' | 'direction' | 'order'>;

interface PaginatedMessages {
  messages: Message[];
  totalCount: number;
}

const { getPayload, queryKeys } = getQueryPayloadAndKey({
  prefix: QueryPrefix.myMessages,
  defaultPayload: {
    type: ItemType.Message,
    limit: MESSAGES_LIMIT,
    offset: 0,
    direction: Direction.DESC,
    order: 'createdAt',
  },
});

export const useMyMessagesQuery = (args?: PayloadOptions) => {
  const payload = useMemo(() => getPayload(args), [args]);

  const isFirstPage = payload.offset === 0;

  const [hasNewMessages, setHasNewMessages] = useState(false);
  const latestMessageIdRef = useRef<number | undefined>(undefined);

  const query = useQuery<{ items: Message[]; count: number }, Error, PaginatedMessages>({
    queryKey: queryKeys.list(payload),
    queryFn: ({ signal }) => getItemsAsync(payload, signal),
    select: (data) => ({ messages: data.items, totalCount: data.count }),
    placeholderData: keepPreviousData,
    staleTime: TIME_INTERVALS.oneMinute,
    refetchInterval: isFirstPage ? TIME_INTERVALS.fiveMinutes : false,
  });

  useEffect(() => {
    if (!isFirstPage || !query.data) {
      return () => {};
    }

    const newLatestId = query.data.messages[0]?.id;

    if (latestMessageIdRef.current !== undefined) {
      const firstMessageChanged = newLatestId !== latestMessageIdRef.current;
      if (firstMessageChanged) {
        queryClient.invalidateQueries({ queryKey: [QueryPrefix.myMessages], exact: false });
      }

      setHasNewMessages(firstMessageChanged);
    }

    latestMessageIdRef.current = newLatestId;
  }, [query.data, isFirstPage]);

  return { ...query, hasNewMessages };
};

export const updateMessageInQueryData = (message: Message, messageOffset: number) => {
  if (!message.id) {
    return;
  }

  const payload = getPayload({ offset: messageOffset });

  return setQueryDataIfOnline<{ items: Message[]; count: number }>(
    queryKeys.list(payload),
    (oldData) => {
      if (!oldData) {
        return oldData;
      }

      const newData = {
        ...oldData,
        items: oldData.items.map((msg) => (msg.id === message.id ? message : msg)),
      };

      return newData;
    }
  );
};

export const invalidateMyMessagesQuery = (args?: PayloadOptions, exact: boolean = false) =>
  queryClient.invalidateQueries({ queryKey: queryKeys.list(getPayload(args)), exact });
